/*
 * Module controlling the list of tasks for the reviewer UI
 */

define([
  "marionette",
  "jquery",
  "messaging/messenger",
  "modules/reviewer/reviewerList/reviewerListView",
  "modules/topicsSplit/topic/topicModel",
  "modules/tasks/tasksModel",
], function (Marionette, $, Messenger, ReviewerListView, TopicModel, TaskModel) {
  "use strict";

  var ReviewerListController = Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "reviewerList";
      this.region = options.region;
      this.taskStatusId = TaskModel.STATUS.NOT_STARTED;
      this._setUpVents();
    },
    show: function (taskStatusId) {
      if (taskStatusId !== undefined && taskStatusId !== null) {
        this.taskStatusId = taskStatusId;
      }
      //get a list of topics that have reviews assigned for this current user
      var login = Messenger.reqres.request("user").get("loginName");
      var topicsToReview = Messenger.reqres.request(
        "search:topics:by:task:assignee:and:status",
        login,
        this.taskStatusId,
      );
      //get a list of tasks assigned for this user
      var tasks = Messenger.reqres.request(
        "tasks:by:assignee:and:status",
        login,
        this.taskStatusId,
      );
      this.topics = topicsToReview.topics();
      var languages = Messenger.reqres.request("kb:languages");
      var languageIdToNameMap = {};
      this.topics = topicsToReview.topics();
      languages.onSynced(function () {
        languages.each(function (language) {
          languageIdToNameMap[language.get("id")] = language.get("name");
        }, this);
        //connect the tasks with the topics
        this._connectTasksAndTopics(tasks, languageIdToNameMap);
      }, this);
      //set a comparator for the topics, since the sortable list usually does this.
      this.topics.comparator = "intentName"; //sort by intent name
      this.view = new ReviewerListView.reviewerListView({ collection: this.topics });

      //make sure the selected task appears selected after a render
      this.view.on(
        "itemview:render",
        function () {
          if (this.selectedTopicModel) {
            this.view.children
              .findByModel(this.selectedTopicModel)
              .toggleSelected(this.selectedTaskId);
          }
        },
        this,
      );

      this.view.on(
        "itemview:selected:review",
        function (itemView, taskId) {
          //deselect the old review
          if (this.selectedTopicModel) {
            this.view.children
              .findByModel(this.selectedTopicModel)
              .toggleSelected(this.selectedTaskId);
          }
          //since each itemView can contain several tasks, store the task Id as well
          this.selectedTopicModel = itemView.model;
          this.selectedTaskId = taskId;
          if (this.selectedTopicModel.get("archived")) {
            Messenger.okDialog("topic.tasks.archivedTopicError");
          } else {
            //send a vent that a review was selected and also pass the taskId
            Messenger.vent.trigger("selected:review", itemView.model, taskId);
          }
        },
        this,
      );
      this.showRegion(this.view, { loading: true });
    },
    save: function () {
      var _this = this;
      //save the collection of topics, but skip any that are "new" because they must be created explicitly by the user
      this.topics.saveCollection({
        skipNew: true,
        success: function () {
          var topicCount = _this.topics.length;
          if (topicCount === 0) {
            Messenger.saveAttempted(true);
          }
          var errorResponse;
          // Still need to save any Tasks associated with new Topics, because they were skipped
          _this.topics.each(function (topic) {
            if (topic.isNew() && topic.tasksCollection) {
              topic.tasksCollection.saveCollection({
                error: function (dummy, response) {
                  errorResponse = response;
                  --topicCount;
                  if (topicCount === 0) {
                    Messenger.setErrorMsgFromResponse(errorResponse);
                    Messenger.saveAttempted(false);
                  }
                },
                success: function () {
                  --topicCount;
                  if (topicCount === 0) {
                    if (errorResponse) {
                      Messenger.setErrorMsgFromResponse(errorResponse);
                      Messenger.saveAttempted(false);
                    } else {
                      Messenger.saveAttempted(true);
                    }
                  }
                },
              });
            } else {
              --topicCount;
              if (topicCount === 0) {
                if (errorResponse) {
                  Messenger.setErrorMsgFromResponse(errorResponse);
                  Messenger.saveAttempted(false);
                } else {
                  Messenger.saveAttempted(true);
                }
              }
            }
          });
        },
      });
    },
    _connectTasksAndTopics: function (tasks, languageIdToNameMap) {
      //first make sure all collections are synced
      this.topics.onSynced(function () {
        tasks.onSynced(function () {
          this.topics.each(function (topic) {
            var topicIntentId = topic.get("intentId");
            //clone the tasks collections and filter by the topicId
            var clonedTasks = tasks.clone();
            //make sure we sort the tasks for this topic by due date (essentially a secondary sort)
            clonedTasks.comparator = "dueDate";
            //remove models from the cloned collection that do not belong to the topic
            clonedTasks.remove(
              clonedTasks.filter(function (task) {
                return task.get("intentId") !== topicIntentId;
              }),
            );
            //link the original task status to the collection as a comparison value to see if the status changes
            //this code assumes that all tasks will have the same status in this list!
            clonedTasks.originalTaskStatus =
              clonedTasks.length > 0 ? clonedTasks.at(0).get("status") : 0; //set it to
            // zero if
            // the
            // collection
            // is empty
            clonedTasks.languageIdToNameMap = languageIdToNameMap;
            //assign the tasks to the topic as if they were loaded as a submodule of topics
            clonedTasks.sort();
            topic.tasksCollection = clonedTasks;
            //we need to add the collection as a submodel
            if (!topic.subModels) {
              topic.subModels = [];
            }
            topic.subModels.push(topic.tasksCollection);
          });
          // Remove topics with no tasks, they are not very useful in this interface
          this.topics.remove(
            this.topics.filter(function (topic) {
              return !topic.tasksCollection || topic.tasksCollection.length === 0;
            }),
          );
          // Look for any tasks with no associated topic, and create a new topic for each one
          tasks.each(function (task) {
            if (task.get("intentId")) {
              return;
            }
            var createTopic = new TopicModel.topic({ intentName: task.get("name") });
            var clonedTasks = new TaskModel.tasks([task.clone()]);
            clonedTasks.originalTaskStatus = task.get("status");
            clonedTasks.languageIdToNameMap = languageIdToNameMap;
            createTopic.tasksCollection = clonedTasks;
            //we need to add the collection as a submodel
            if (!createTopic.subModels) {
              createTopic.subModels = [];
            }
            createTopic.subModels.push(clonedTasks);
            this.topics.add(createTopic);
          }, this);
          this.view.render();
        }, this);
      }, this);
    },
    _setUpVents: function () {
      //handle re-showing the list when the user toggles which task status to look at
      Messenger.vent.on(
        "show:reviewer:list",
        function (statusId) {
          delete this.selectedTopicModel; //no task is selected
          delete this.selectedTaskId;
          this.taskStatusId = statusId;
          this.show(statusId);
        },
        this,
      );
      Messenger.vent.on(
        "show:resubmitted:task",
        function (task) {
          task.saveModel();
        },
        this,
      );
    },
  });

  return ReviewerListController;
});
